import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { StylePanelProps } from '@/lib/stylePanel'
import useResponsivePadding from '@/lib/useResponsivePadding'
import { imageServiceURl } from '@/utilities/imageService'
import { linkifyPhoneNumbers } from '@/utilities/linkifyPhone'

import { PhnxVideoHolder } from '../../01-atoms/VideoHolder/VideoHolder'
import styles from './Herobanner.module.css'

export type HerobannerProps = {
  title?: string
  subtitle?: string
  asset?:
    | {
        src: string
        alt: string
        focus?: string
        wash_opacity?: number
      }
    | undefined
  video?:
    | {
        id: number
        wash_opacity?: number
      }
    | undefined
  featureImage?: {
    src: string
    alt: string
    focus?: string
  }
  featureVideo?:
    | {
        id: string
        type: 'youtube' | 'vimeo' | 'asset'
      }
    | undefined
  button?: React.ReactNode
  layout: 'default' | 'centered' | 'service' | 'article'
  cropImage?: boolean
  titleSize?: number
  subtitleSize?: number
} & StylePanelProps

export const PhnxHerobanner: React.FC<HerobannerProps> = ({
  title,
  subtitle,
  asset,
  button,
  featureImage,
  video,
  featureVideo,
  layout,
  padding,
  backgroundColor,
  textColor,
  cropImage,
  titleSize,
  subtitleSize,
}) => {
  const paddingStyles = useResponsivePadding(padding)

  const isDesktop = useMediaQuery({ query: '(min-width: 600px)' })
  const [imageUri, setImageUri] = useState<string>('')

  useEffect(() => {
    if (asset?.src) {
      const imageHeightWidth = isDesktop ? '1500x500' : '700x500'
      let imageUri = `${asset.src}/m/filters:quality(80)`
      if (cropImage) {
        imageUri = `${asset.src}/m/${imageHeightWidth}/filters:quality(80)`
      }
      if (layout === 'article' && !isDesktop) {
        const imageHeightWidth = '400x100'
        imageUri = `${asset.src}/m/${imageHeightWidth}/filters:quality(100)`
      }

      // if focus add focal point to image
      if (asset?.focus) {
        imageUri = `${imageUri}:focal(${asset.focus})`
      }
      setImageUri(imageUri)
    }
  }, [asset, isDesktop, cropImage])

  const imageClass = asset?.src ? styles['has-image'] : ''

  let wash_opacity = asset?.wash_opacity ? asset.wash_opacity : 0

  const style = {
    ...paddingStyles,
    color: textColor,
  }

  let featuredImageArray = imageServiceURl(featureImage ? featureImage.src : '')

  const titleFontSize = titleSize ? `${titleSize}px` : ''
  const subtitleFontSize = subtitleSize ? `${subtitleSize}px` : ''

  return (
    <div
      className={`${styles['herobanner']} ${imageClass} ${styles[`layout-${layout}`]}  ${styles[imageUri === '' && video === undefined ? 'noBackgroundImage' : '']}`}
      style={{
        backgroundColor: backgroundColor ? backgroundColor : 'black',
        backgroundImage: `url("${imageUri}")`,
        ...(imageUri &&
          wash_opacity && {
            borderImage: `linear-gradient(hsl(0 0% 0% / ${wash_opacity}), hsl(0 0% 0% /${wash_opacity})) fill 1`,
          }),
      }}
      data-testid="herobanner"
      data-test-layout={layout}
    >
      {video && (
        <div className={styles['video-container']}>
          <iframe
            src={`https://player.vimeo.com/video/${video.id}?autoplay=1&loop=1&background=1&mute=1`}
            loading="lazy"
            title="video"
            style={{ opacity: video.wash_opacity }}
          />
        </div>
      )}
      <div className="container">
        <div className={styles['text-container']}>
          <div style={style} className="stylepanel">
            <style jsx>{`
              stylepanel {
                ${paddingStyles}
              }
            `}</style>
            {featureVideo && featureVideo.id && (
              <div className={styles['herobanner-video']}>
                <div className={styles['herobanner-video-text']}>
                  {title && layout !== 'service' && (
                    <h1 style={{ fontSize: titleFontSize }}>{title}</h1>
                  )}

                  {title && layout === 'service' && (
                    <div className={`title ${styles.title}`}>
                      <span>{title}</span>
                    </div>
                  )}
                  {subtitle && (
                    <h2
                      style={{
                        fontSize: subtitleFontSize,
                      }}
                    >
                      {linkifyPhoneNumbers(subtitle)}
                    </h2>
                  )}
                  {button && <div className={styles['button-container']}>{button}</div>}
                </div>
                <div className={styles['feature-video']}>
                  <PhnxVideoHolder
                    video={[
                      {
                        url: featureVideo.id,
                        type: featureVideo.type,
                      },
                    ]}
                  />
                </div>
              </div>
            )}

            {featureImage && featureImage.src && (
              <div className={styles['feature-asset']}>
                <Image
                  src={featuredImageArray.imgURL}
                  alt={featureImage.alt}
                  height={featuredImageArray.currentImageHeight || 0}
                  width={featuredImageArray.currentImageWidth || 0}
                />
              </div>
            )}
            {featureVideo === undefined && (
              <>
                {title && layout !== 'service' && (
                  <h1 style={{ fontSize: titleFontSize }}>{title}</h1>
                )}

                {title && layout === 'service' && (
                  <div className={`title ${styles.title}`}>
                    <span>{title}</span>
                  </div>
                )}
                {subtitle && (
                  <h2 style={{ fontSize: subtitleFontSize }}>{linkifyPhoneNumbers(subtitle)}</h2>
                )}
                {button && button !== undefined && (
                  <div className={styles['button-container']}>{button}</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
